import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { Field, type InjectedFormProps, reduxForm, type WrappedFieldProps } from "redux-form";

import {
  getRetainersQuantityFromCourseOptionTag,
  isAdditionalAlignersCourse,
  isChildrenCourse,
  isRetainersCourse,
} from "~/common/courses";
import { initializeBaseInstruction } from "~/common/instructions";
import { remoteLog } from "~/common/logging";
import { getLastCorrection, isPatient } from "~/common/patient";
import { INITIAL_VALUES_PRESCRIPTION } from "~/common/prescription";
import { Label } from "~/components/ui/label";
import { TextArea } from "~/components/ui/textarea";
import { selectCorrectionFormValues } from "~/slices/redux-form";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";

import PlanCorrectionTeethCheck from "../3d_plan_correction/3d_plan_correction_teeth_check";
import { RetainersQuantityGroup } from "../3d_plan_retainers/3d_plan_retainers_page";
import { RecipeCanines } from "./recipe/recipe-canines";
import { RecipeCloseAllGaps } from "./recipe/recipe-close-all-gaps";
import { RecipeElastics } from "./recipe/recipe-elastics";
import { RecipeIncisors } from "./recipe/recipe-incisors";
import { RecipeMolars, RecipeMolarsChildren } from "./recipe/recipe-molars";
import { RecipeTreatArches } from "./recipe/recipe-treat-arches";

const mapStateToProps = (state: RootState) => {
  return {
    patient: state.patient,
    formValues: selectCorrectionFormValues(state),
  };
};

// eslint-disable-next-line @typescript-eslint/ban-types
type RecipeFormData = {};
type RecipeFormOwnProps = PropsFromRedux & {
  course_id: TPatient["course"]["course_id"] | null | undefined;
};
type RecipeFormProps = RecipeFormOwnProps & InjectedFormProps<RecipeFormData, RecipeFormOwnProps>;

class RecipeForm extends Component<RecipeFormProps> {
  constructor(props: RecipeFormProps) {
    super(props);
    this.resetForm = this.resetForm.bind(this);
    this.resetElasticsOptionsRight = this.resetElasticsOptionsRight.bind(this);
    this.resetElasticsOptionsLeft = this.resetElasticsOptionsLeft.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "3d_plan_correction_form");
  }

  resetForm(...arg: string[]) {
    arg.forEach((field) => this.props.change(field, false));
  }

  resetElasticsOptionsLeft(e: React.ChangeEvent<HTMLInputElement>, obj: string) {
    const elastics_left = this.props.formValues?.elastics_options;
    if (e.target.checked === false) {
      for (const key in elastics_left) {
        if (key.startsWith("2")) {
          delete elastics_left[key];
        } else if (key.startsWith("3")) {
          delete elastics_left[key];
        }
      }
      return elastics_left;
    }
    this.props.change(obj, elastics_left);
  }

  resetElasticsOptionsRight(e: React.ChangeEvent<HTMLInputElement>, obj: string) {
    const elastics_right = this.props.formValues?.elastics_options;
    if (e.target.checked === false) {
      for (const key in elastics_right) {
        if (key.startsWith("1")) {
          delete elastics_right[key];
        } else if (key.startsWith("4")) {
          delete elastics_right[key];
        }
      }
      return elastics_right;
    }
    this.props.change(obj, elastics_right);
  }

  render() {
    const { change, course_id } = this.props;
    const {
      quantity,
      upper_arch_action,
      lower_arch_action,
      close_all_gaps,
      midline,
      elastics_recoil_right,
      elastics_recoil_left,
      vertical_overlap,
      occlusal_plane,
      occlusal_plane_action,
    } = this.props.formValues ?? {};
    let elastics_options: number[] = [];

    if (isPatient(this.props.patient)) {
      const prescription = getLastCorrection(this.props.patient).prescription;

      if (prescription) {
        elastics_options = Object.keys(prescription.elastics_options).map((i) => parseInt(i));
      }
    }

    return (
      <div>
        {isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id) ? null : (
          <h3 className="block" style={{ fontWeight: "900" }}>
            <FormattedMessage id="BLOCKHEAD_INSTRUCTIONS" />
          </h3>
        )}

        {isAdditionalAlignersCourse(course_id) ? null : (
          <RecipeTreatArches
            course_id={course_id}
            upper_arch_action={upper_arch_action}
            lower_arch_action={lower_arch_action}
            occlusal_plane={occlusal_plane}
            occlusal_plane_action={occlusal_plane_action}
            resetForm={this.resetForm}
            change={change}
          />
        )}

        {isRetainersCourse(course_id) ? (
          <RetainersQuantityGroup
            quantity={quantity}
            onQuantityChange={(newQuantity) => this.props.change("quantity", newQuantity)}
            className="form-group"
          />
        ) : null}

        {isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id) ? null : (
          <RecipeIncisors
            course_id={course_id}
            vertical_overlap={vertical_overlap}
            midline={midline}
          />
        )}

        {isRetainersCourse(course_id) ||
        isChildrenCourse(course_id) ||
        isAdditionalAlignersCourse(course_id) ? null : (
          <>
            <RecipeCanines />
            <RecipeMolars />
          </>
        )}

        {isChildrenCourse(course_id) ? <RecipeMolarsChildren /> : null}

        {isRetainersCourse(course_id) ||
        isChildrenCourse(course_id) ||
        isAdditionalAlignersCourse(course_id) ? null : (
          <>
            <RecipeCloseAllGaps close_all_gaps={close_all_gaps} change={change} />
            <RecipeElastics
              elastics_recoil_right={elastics_recoil_right}
              elastics_recoil_left={elastics_recoil_left}
              elastics_options={elastics_options}
              resetElasticsOptionsRight={this.resetElasticsOptionsRight}
              resetElasticsOptionsLeft={this.resetElasticsOptionsLeft}
              resetForm={this.resetForm}
            />
          </>
        )}

        {isChildrenCourse(course_id) ? (
          <div>
            <Label id="EXTRA_TEETHING" readOnly>
              <FormattedMessage id="EXTRA_TEETHING" />
            </Label>

            <PlanCorrectionTeethCheck name="teething" ariaLabelledBy="EXTRA_TEETHING" />
          </div>
        ) : null}

        {isChildrenCourse(course_id) ||
        isRetainersCourse(course_id) ||
        isAdditionalAlignersCourse(course_id) ? null : (
          <div>
            <Label id="EXTRA_NOMOVE" readOnly>
              <FormattedMessage id="EXTRA_NOMOVE" />
            </Label>

            <PlanCorrectionTeethCheck name="dont_move" ariaLabelledBy="EXTRA_NOMOVE" />

            <Label id="EXTRA_NOLOCK" readOnly>
              <FormattedMessage id="EXTRA_NOLOCK" />
            </Label>

            <PlanCorrectionTeethCheck name="avoid_locks" ariaLabelledBy="EXTRA_NOLOCK" />

            <Label id="EXTRA_EXTRACT" readOnly>
              <FormattedMessage id="EXTRA_EXTRACT" />
            </Label>

            <PlanCorrectionTeethCheck name="extractions" ariaLabelledBy="EXTRA_EXTRACT" />
          </div>
        )}

        <div className="form-group">
          <Label id="correction-add-comment-label" htmlFor="correction-add-comment">
            <FormattedMessage id="EXTRA_COMMENTS" />
          </Label>

          <Field
            name="comment"
            component={ExtraCommentsTextArea}
            format={(v: string | undefined) => (v ? v : "")}
          />
        </div>
      </div>
    );
  }
}

function ExtraCommentsTextArea(formProps: WrappedFieldProps) {
  return <TextArea {...formProps.input} id="correction-add-comment" />;
}

const RecipeFormWithReduxForm = reduxForm<RecipeFormData, RecipeFormOwnProps>({
  form: "correction",
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  updateUnregisteredFields: true,
})(RecipeForm);

const RecipeFormWithReduxFormInitialValues = connect(({ patient }: RootState) => {
  let initialValues = INITIAL_VALUES_PRESCRIPTION;

  if (isPatient(patient)) {
    const lastCorrection = getLastCorrection(patient);

    if (lastCorrection.prescription && !lastCorrection.order_options.can_order_correction) {
      const quantity = isRetainersCourse(patient.course.course_id)
        ? getRetainersQuantityFromCourseOptionTag(patient.course.course_option_tag)
        : null;
      initialValues = initializeBaseInstruction({ ...lastCorrection.prescription, quantity });
    }
  }

  return { initialValues };
})(RecipeFormWithReduxForm);

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(RecipeFormWithReduxFormInitialValues);
